import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';

import {BasesColumnEntityType} from '../bases-columns/BasesColumnConstants';
import {customColumnDataTypeToEquivalentFieldType} from '../custom-columns/CustomColumnConstants';

/**
 * This is the list of icons used in structured table column label prefixes.
 */
const ICON_ALIAS = 'at-mention';
const ICON_ARCHIVED = 'archive';
const ICON_ATTACHMENT = 'attachment';
const ICON_BOOLEAN = 'boolean';
const ICON_CONTAINER = 'vial';
const ICON_DATE = 'calendar';
const ICON_DATETIME = 'eln-timestamp';
const ICON_DELETED = 'delete';
const ICON_DNA = 'helix';
const ICON_AA_SEQUENCE = 'protein';
export const ICON_COMPUTED_FIELD = 'function';
export const ICON_CONVERTED_FROM_LINK = 'converted-from';
const ICON_JSON = 'json';
const ICON_POSITION = 'pin';
const ICON_SELECTOR = 'single-select';
const ICON_MULTISELECT = 'multi-select';
const ICON_NUMBER = 'number';
const ICON_PROCESS_OUTPUT = 'process-output';
const ICON_PROCESS_TASK = 'process-task';
const ICON_SEARCH = 'search';
const ICON_STORABLE = 'fridge';
const ICON_TEXT = 'text';
const ICON_USER = 'user';
const ICON_TEAM = 'team';
const ICON_UNIT_REFERENCE = 'ruler';

/**
 * Label prefix icon mappings for table column types.
 *
 * null means that no icon is shown.
 */
const NON_TAG_VALUE_ICONS: {
  [type in Exclude<
    TableColumnType,
    TableColumnType.TAG_VALUE | TableColumnType.CUSTOM | TableColumnType.BASES
  >]: BenchlingIconName | null;
} = {
  [TableColumnType.STRING]: ICON_TEXT,
  [TableColumnType.BIOENTITY_NAME]: ICON_TEXT,
  [TableColumnType.BATCH]: ModelTypeConstants.BATCH,
  [TableColumnType.DNA_BASES]: ICON_DNA,
  [TableColumnType.RNA_BASES]: ICON_DNA,
  [TableColumnType.DNA_HELM]: ICON_DNA,
  [TableColumnType.RNA_HELM]: ICON_DNA,
  [TableColumnType.DNA_IDT]: ICON_DNA,
  [TableColumnType.RNA_IDT]: ICON_DNA,
  [TableColumnType.SMILES]: FolderItemIcon.SmallMolecule,
  [TableColumnType.STAGE_RUN]: ModelTypeConstants.BATCH,
  [TableColumnType.EXISTING_BIOENTITY]: ModelTypeConstants.BIOENTITY_MIXIN,
  [TableColumnType.ADC_ENTITY_INPUT]: ModelTypeConstants.BIOENTITY_MIXIN,
  [TableColumnType.BIOENTITY_SELECTOR]: ModelTypeConstants.BIOENTITY_MIXIN,
  [TableColumnType.ALIAS]: ICON_ALIAS,
  [TableColumnType.STUDIES]: ModelTypeConstants.STUDY,
  [TableColumnType.NUMBER_OF_CONTAINERS]: ICON_NUMBER,
  [TableColumnType.CONTAINERS]: ICON_CONTAINER,
  [TableColumnType.PARENT_STORABLE]: ICON_STORABLE,
  [TableColumnType.POSITION]: ICON_POSITION,
  [TableColumnType.QUANTITY]: ICON_NUMBER,
  [TableColumnType.CONCENTRATION]: ICON_NUMBER,
  [TableColumnType.SAMPLE]: ModelTypeConstants.BIOENTITY_MIXIN,
  [TableColumnType.PLATE]: ModelTypeConstants.PLATE,
  [TableColumnType.BOX]: ModelTypeConstants.BOX,
  [TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN]: null,
  [TableColumnType.PROCEDURE_PARAMETER_MIN_MAX]: null,
  [TableColumnType.PARTIES]: ICON_TEAM,
  [TableColumnType.PROCESS_TASK]: ICON_PROCESS_TASK,
  [TableColumnType.PROCESS_TASK_ASSIGNEE]: ICON_USER,
  [TableColumnType.PROCESS_TASK_STATUS]: ICON_MULTISELECT,
  [TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE]: ICON_DATE,
  [TableColumnType.PROCESS_OUTPUT]: ICON_PROCESS_OUTPUT,
  [TableColumnType.PROCEDURE]: null,
  [TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR]: null,
  [TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE]: null,
  [TableColumnType.PROCEDURE_CONDITION_REPLICATES]: null,
  [TableColumnType.TEST_DEFINITION]: null,
  [TableColumnType.SAMPLE_RESTRICTION_STATUS]: ICON_MULTISELECT,
  [TableColumnType.MEASUREMENT_AMOUNT]: ICON_NUMBER,
  [TableColumnType.INGREDIENT_CATALOG_ID]: ICON_TEXT,
  [TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT]: ICON_TEXT,
  [TableColumnType.INGREDIENT_NOTES]: ICON_TEXT,
  [TableColumnType.ANONYMOUS_TAG_VALUE]: null,
  [TableColumnType.EXPERIMENTAL_ROLE]: null,
  [TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT]: null,
  [TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP]: null,
  [TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP]: null,
  [TableColumnType.FILL_PLATE_TRANSFER_SOURCE_ICON]: null,
  [TableColumnType.FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP]: null,
  [TableColumnType.PLATE_MAP_MATERIAL_INPUT]: null,
  [TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER]: null,
  [TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER]: null,
  [TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR]: null,
  [TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT]: null,
  [TableColumnType.BULK_ASSEMBLY_LENGTH]: null,
  [TableColumnType.BULK_ASSEMBLY_CONSTRUCT_NAME]: null,
  [TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS]: null,
  [TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR]: null,
  [TableColumnType.BULK_ASSEMBLY_PRIMER]: null,
  [TableColumnType.BULK_ASSEMBLY_PRIMER_BASES]: null,
  [TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME]: null,
  [TableColumnType.UNIT_SELECTOR]: ICON_MULTISELECT,
  [TableColumnType.UNIT_REFERENCE]: ICON_UNIT_REFERENCE,
  [TableColumnType.DEDICATED_LOOKUP]: ICON_SEARCH,
  [TableColumnType.BOOLEAN]: ICON_BOOLEAN,
  [TableColumnType.FULFILLABLE_STATUS]: ICON_MULTISELECT,
  [TableColumnType.REQUEST_TASK_SELECTION]: ICON_MULTISELECT,
  [TableColumnType.SAMPLE_GROUP_SAMPLE]: ModelTypeConstants.BIOENTITY_MIXIN,
  [TableColumnType.STAGE_RUN_SAMPLE]: ModelTypeConstants.BIOENTITY_MIXIN,
  [TableColumnType.OPTION_SELECTOR]: ICON_MULTISELECT,
  [TableColumnType.OPTION_SEARCH_SELECTOR]: ICON_MULTISELECT,
  [TableColumnType.LOOKUP_CONFIG]: ICON_SEARCH,
  [TableColumnType.PLATE_LAYOUT_SELECTOR]: ICON_MULTISELECT,
  [TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN]: FolderItemIcon.Entry,
  [TableColumnType.ROW_DELEGATE]: null,
  [TableColumnType.COMPLEX_POLYMER_COMPONENT]: ModelTypeConstants.BIOENTITY_MIXIN,
  [TableColumnType.COMPLEX_POLYMER_CONNECTIONS]: ICON_TEXT,
  [TableColumnType.OLIGO_DUPLEX_OFFSET]: ICON_NUMBER,
  [TableColumnType.SEQUENCE_TOPOLOGY]: ICON_MULTISELECT,
  // Default to archived icon, though we check for whether the field was deleted or archived in
  // `columnDefinitionToIcon`
  [TableColumnType.MISSING_FIELD]: ICON_ARCHIVED,
  [TableColumnType.ANTIBODY_DOMAIN]: ModelTypeConstants.BIOENTITY_MIXIN,
  [TableColumnType.MOLECULE_VERSION]: ICON_DATETIME,
  [TableColumnType.PROTEIN_NESTED_COMPONENT]: ModelTypeConstants.BIOENTITY_MIXIN,
};

/**
 * Label prefix icon mappings for when column type is declared by a schema field.
 */
const TAG_VALUE_TYPE_ICONS: {
  [type in FieldType]: BenchlingIconName;
} = {
  [FieldType.AA_PART_LINK]: ModelTypeConstants.BIOENTITY_MIXIN,
  [FieldType.BASE_MOLECULE_LINK]: ModelTypeConstants.BIOENTITY_MIXIN,
  [FieldType.ASSAY_REQUEST_LINK]: ModelTypeConstants.BIOENTITY_MIXIN,
  [FieldType.ASSAY_RESULT_LINK]: ModelTypeConstants.BIOENTITY_MIXIN,
  [FieldType.ASSAY_RUN_LINK]: ModelTypeConstants.BIOENTITY_MIXIN,
  [FieldType.BATCH_LINK]: ModelTypeConstants.BATCH,
  [FieldType.BLOB_LINK]: ICON_ATTACHMENT,
  [FieldType.BOOLEAN]: ICON_BOOLEAN,
  [FieldType.DATE]: ICON_DATE,
  [FieldType.DATETIME]: ICON_DATETIME,
  [FieldType.ENTRY_LINK]: FolderItemIcon.Entry,
  [FieldType.BIOENTITY_LINK]: ModelTypeConstants.BIOENTITY_MIXIN,
  [FieldType.FLOAT]: ICON_NUMBER,
  [FieldType.INTEGER]: ICON_NUMBER,
  [FieldType.JSON]: ICON_JSON,
  [FieldType.LONG_TEXT]: ICON_TEXT,
  [FieldType.PART_LINK]: ModelTypeConstants.BIOENTITY_MIXIN,
  [FieldType.SAMPLE_LINK]: ModelTypeConstants.BIOENTITY_MIXIN,
  [FieldType.SELECTOR]: ICON_MULTISELECT,
  [FieldType.STORABLE_LINK]: ICON_STORABLE,
  [FieldType.TEXT]: ICON_TEXT,
  [FieldType.TRANSCRIPTION_LINK]: ModelTypeConstants.BIOENTITY_MIXIN,
  [FieldType.TRANSLATION_LINK]: ModelTypeConstants.BIOENTITY_MIXIN,
};

/**
 *
 * @param columnDefinition the column to find an icon for.
 * @returns an icon value string or null if no icon was found for the column def.
 */
export default function columnDefinitionToIcon(columnDefinition: ColumnDefinition): BenchlingIconName | null {
  if (
    (columnDefinition.type === TableColumnType.TAG_VALUE &&
      (columnDefinition.tagSchemaFieldIsComputed || columnDefinition.tagSchemaFieldIsSnapshot)) ||
    (columnDefinition.type === TableColumnType.MEASUREMENT_AMOUNT && columnDefinition.isComputed)
  ) {
    return ICON_COMPUTED_FIELD;
  }
  if (
    columnDefinition.type === TableColumnType.TAG_VALUE &&
    columnDefinition.tagSchemaFieldIsConvertedFromLink
  ) {
    return ICON_CONVERTED_FROM_LINK;
  }

  if (columnDefinition.type === TableColumnType.MISSING_FIELD) {
    return columnDefinition.isArchived ? ICON_ARCHIVED : ICON_DELETED;
  }

  if (columnDefinition.type === TableColumnType.CUSTOM) {
    return TAG_VALUE_TYPE_ICONS[
      customColumnDataTypeToEquivalentFieldType(columnDefinition.customColumnConfig.dataType)
    ];
  }

  if (columnDefinition.type === TableColumnType.BASES) {
    return columnDefinition.basesColumnConfig.entityType === BasesColumnEntityType.AA_SEQUENCE
      ? ICON_AA_SEQUENCE
      : ICON_DNA;
  }

  if (
    columnDefinition.type === TableColumnType.TAG_VALUE &&
    columnDefinition.tagSchemaFieldType === FieldType.SELECTOR
  ) {
    return columnDefinition.schemaField.isMulti ? ICON_MULTISELECT : ICON_SELECTOR;
  }

  return (
    (columnDefinition.type === TableColumnType.TAG_VALUE
      ? TAG_VALUE_TYPE_ICONS[columnDefinition.schemaField.fieldType]
      : NON_TAG_VALUE_ICONS[columnDefinition.type]) ?? null
  );
}
